import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useSetRecoilState } from 'recoil';
import { Table } from 'components/tables/ItemsTable/styledComponents';
import { LIST_RMA_ORDERS } from 'connectors/queries/order';
import { loadingState } from 'state/atoms';
import TableDetails from 'components/TableDetails';
import { buildOrderTable } from 'pages/CaseDetails/utils/order';
import { NoResultMessage } from 'components';
import { RmaDetailTable } from 'pages/CaseDetails/styledComponents';
import { TextStatus, PairOrderColumn, PairOrderContent } from './styledComponents';
import {
  getFormattedDate,
  getOrderSerialNumber,
  getReturnRows,
  getShippingMethod,
  getShippingStatus,
  getTrackingNumber,
  isDifferentPartNumber,
} from './helpers';
import { useFormatPrice } from 'utils/hooks/useFormatPrice';

const OrdersTable = ({ order, accountId }) => {
  const setLoading = useSetRecoilState(loadingState);
  const formatPrice = useFormatPrice();

  const { data, loading, error } = useQuery(LIST_RMA_ORDERS, {
    variables: {
      accountId,
      salesOrderNumber: order,
    },
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);
  const groups = React.useMemo(() => buildOrderTable(data), [data]);
  const orderCurrencyCode = data?.getAccount.getRmaOrder.currencyCode;

  if (error) {
    return (
      <NoResultMessage>
        We were unable to collect the details for this order. Please, try again or contact the RMA
        team at RMA@calix.com if the problem persists.
      </NoResultMessage>
    );
  }
  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>Part Number</th>
            <th>Returned</th>
            <th>Shipped</th>
            <th style={{ width: '10rem' }}>Price</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan="10">
              {Object.keys(groups).length === 0 && !loading && (
                <NoResultMessage>Your RMA Order doesn’t have items.</NoResultMessage>
              )}
            </td>
          </tr>
        </tbody>
        {Object.keys(groups).map((partNumber) => {
          const group = groups[partNumber];

          return (
            <TableDetails
              key={partNumber + group.description}
              title={partNumber}
              description={group.description}
              rows={[
                `${group.returned || 0} of ${group.returnedTotal}`,
                `${group.shipped || 0} of ${group.shippedTotal}`,
                formatPrice(group.extendedPrice || 0, orderCurrencyCode),
              ]}
            >
              <tr>
                <td colSpan="10">
                  <RmaDetailTable>
                    {group.pairs.map((pair, index) => {
                      return (
                        <TableDetails
                          lastLineWidth="10rem"
                          key={`${order}-${partNumber}-${index}`}
                          rows={getReturnRows(pair, formatPrice, orderCurrencyCode)}
                        >
                          <tr>
                            <PairOrderColumn>
                              <PairOrderContent style={{ marginLeft: '33px' }}>
                                {isDifferentPartNumber(pair) && (
                                  <TextStatus>
                                    Replacement Part Number: {pair.order?.partNumber}
                                  </TextStatus>
                                )}
                                <TextStatus>
                                  Replacement Serial #: {getOrderSerialNumber(pair)}
                                </TextStatus>
                                <TextStatus>
                                  Scheduled Shipment Date:{' '}
                                  {getFormattedDate(pair, pair.order?.scheduledShipDate)}
                                </TextStatus>
                                <TextStatus>Shipping Method: {getShippingMethod(pair)}</TextStatus>
                              </PairOrderContent>
                            </PairOrderColumn>
                            <PairOrderColumn>
                              <PairOrderContent style={{ marginLeft: '11px' }}>
                                <TextStatus>Shipping Status: {getShippingStatus(pair)}</TextStatus>
                                <TextStatus>
                                  Actual Shipment Date:{' '}
                                  {getFormattedDate(pair, pair.order?.actualShipDate)}
                                </TextStatus>
                                <TextStatus>Tracking Number: {getTrackingNumber(pair)}</TextStatus>
                              </PairOrderContent>
                            </PairOrderColumn>
                          </tr>
                        </TableDetails>
                      );
                    })}
                  </RmaDetailTable>
                </td>
              </tr>
            </TableDetails>
          );
        })}
      </Table>
    </>
  );
};

export default OrdersTable;
